// The arithmetic behind the Pastoral Care summary. Pure functions over the four
// record shapes, so the screen only lays the answers out.

import { countBy, lastMonths, inMonth, inYear, daysUntil, dateOf, isoOf } from '../../utils/summaryStats';
import type { NamedCount, MonthBucket } from '../../utils/summaryStats';

export interface VisitRequestLike {
  recordId?: string;
  requestedBy?: string;
  requestType?: string;
  preferredDate?: string;
  assignedPastor?: string;
  location?: string;
  /**
   * The mobile app writes PENDING / SCHEDULED / COMPLETED / CANCELLED; the back
   * office writes ACTIVE (shown as Scheduled) / IN_PROGRESS / COMPLETED.
   */
  status?: string;
  // Aliases mapVisit() adds on read.
  memberName?: string;
  date?: string;
  purpose?: string;
}

export interface PastoralVisitLike {
  date?: string;
  memberName?: string;
  pastorName?: string;
  location?: string;
  purpose?: string;
  status?: string;
}

export interface CounselingLike {
  date?: string;
  memberName?: string;
  pastorName?: string;
  type?: string;
  /** Ongoing / Resolved… as typed, or COMPLETED once closed from the list. */
  sessionStatus?: string;
  status?: string;
}

export interface SickLogLike {
  recordId?: string;
  date?: string;
  memberName?: string;
  pastorName?: string;
  location?: string;
  followUpDate?: string;
  /** ACTIVE / IN_PROGRESS while under care, COMPLETED once closed. */
  status?: string;
}

const isVoid = (r: { status?: string }) => String(r.status || '').toUpperCase() === 'VOID';

const STAGE_WORDS: Record<string, string> = {
  PENDING: 'Pending',
  ACTIVE: 'Scheduled',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled'
};

/** A request's stage in words; a blank status is Pending, as the server defaults it. */
export const requestStage = (r: VisitRequestLike): string => {
  const s = String(r.status || 'PENDING').trim();
  return STAGE_WORDS[s.toUpperCase()] || s;
};

/** A request still needs someone to act on it. */
export const isOpenRequest = (r: VisitRequestLike): boolean =>
  !['Completed', 'Cancelled'].includes(requestStage(r));

/** A counselling session counts as closed once completed, resolved, concluded or referred on. */
export const isClosedSession = (c: CounselingLike): boolean =>
  /complet|resolv|conclud|referr|closed/i.test(String(c.sessionStatus || ''));

const sessionStatusOf = (c: CounselingLike) =>
  (String(c.sessionStatus || '').toUpperCase() === 'COMPLETED' ? 'Completed' : c.sessionStatus);

export interface SickWatch extends SickLogLike {
  /** Days until the follow-up (negative when overdue), or null with none set. */
  dueIn: number | null;
}

export interface PastorLoad {
  name: string;
  visits: number;
  counseling: number;
  sick: number;
  openRequests: number;
  total: number;
}

export interface PastoralSummary {
  openRequests: VisitRequestLike[];
  unassigned: number;
  requestsByType: NamedCount[];
  requestsByStage: NamedCount[];
  requestTotal: number;
  visitsThisMonth: number;
  visitsThisYear: number;
  visitMonths: MonthBucket[];
  sessionsThisMonth: number;
  ongoingSessions: number;
  sessionsByType: NamedCount[];
  sessionsByStatus: NamedCount[];
  sessionTotal: number;
  underCare: number;
  followUpsOverdue: number;
  followUpsDueSoon: number;
  /** Sick members under care with no follow-up set, or one overdue or due within 7 days. */
  sickWatch: SickWatch[];
  byPastor: PastorLoad[];
}

export const pastoralSummary = (
  requests: VisitRequestLike[], visits: PastoralVisitLike[], sessions: CounselingLike[], sick: SickLogLike[],
  now: Date = new Date()
): PastoralSummary => {
  const reqs = requests.filter((r) => !isVoid(r));
  const done = visits.filter((v) => !isVoid(v));
  const counsel = sessions.filter((c) => !isVoid(c) && String(c.sessionStatus || '').toUpperCase() !== 'VOID');
  const ill = sick.filter((s) => !isVoid(s));

  const open = reqs.filter(isOpenRequest)
    .sort((a, b) => String(dateOf(a.preferredDate || a.date) || '9999').localeCompare(String(dateOf(b.preferredDate || b.date) || '9999')));
  const underCare = ill.filter((s) => String(s.status || '').toUpperCase() !== 'COMPLETED');
  const watched: SickWatch[] = underCare.map((s) => ({ ...s, dueIn: daysUntil(s.followUpDate, now) }));
  const sickWatch = watched
    .filter((s) => s.dueIn === null || s.dueIn <= 7)
    // Overdue first, then soonest; those with no follow-up date last.
    .sort((a, b) => (a.dueIn ?? Infinity) - (b.dueIn ?? Infinity));

  // Everyone who has carried out care, and how much of each kind.
  const loads = new Map<string, PastorLoad>();
  const bump = (name: unknown, field: 'visits' | 'counseling' | 'sick' | 'openRequests') => {
    const k = String(name || '').trim() || 'Not assigned';
    const cur = loads.get(k) || { name: k, visits: 0, counseling: 0, sick: 0, openRequests: 0, total: 0 };
    cur[field] += 1;
    cur.total += 1;
    loads.set(k, cur);
  };
  done.forEach((v) => bump(v.pastorName, 'visits'));
  counsel.forEach((c) => bump(c.pastorName, 'counseling'));
  ill.forEach((s) => bump(s.pastorName, 'sick'));
  open.forEach((r) => bump(r.assignedPastor, 'openRequests'));

  return {
    openRequests: open,
    unassigned: open.filter((r) => !String(r.assignedPastor || '').trim()).length,
    requestsByType: countBy(reqs, (r) => r.requestType || r.purpose),
    requestsByStage: countBy(reqs, requestStage),
    requestTotal: reqs.length,
    visitsThisMonth: inMonth(done, (v) => v.date, now).length,
    visitsThisYear: inYear(done, (v) => v.date, now.getFullYear()).length,
    visitMonths: lastMonths(done.filter((v) => (dateOf(v.date) || '') <= isoOf(now)), (v) => v.date, 12, now),
    sessionsThisMonth: inMonth(counsel, (c) => c.date, now).length,
    ongoingSessions: counsel.filter((c) => !isClosedSession(c)).length,
    sessionsByType: countBy(counsel, (c) => c.type),
    sessionsByStatus: countBy(counsel, sessionStatusOf),
    sessionTotal: counsel.length,
    underCare: underCare.length,
    followUpsOverdue: watched.filter((s) => s.dueIn !== null && s.dueIn < 0).length,
    followUpsDueSoon: watched.filter((s) => s.dueIn !== null && s.dueIn >= 0 && s.dueIn <= 7).length,
    sickWatch,
    byPastor: Array.from(loads.values())
      .filter((p) => p.name !== 'Not assigned')
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
  };
};
